import { createFileRoute, redirect } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { questionnaireDashboardQueryOptions } from '#/api/questionnaires'
import { formatCode } from '#/lib/format'
import { useCan } from '#/user/me'
import { RoutePending } from '#/components/route-pending'
import { PageContainer } from '#/components/page-container'
import { PageHeader } from '#/components/page-header'
import { RouteError } from '#/components/route-error'
import { ActionsPane } from '#/components/upload/actions-pane'
import { uploadDestinationOf } from '#/components/upload/upload-queue-state'
import type { UploadDestination } from '#/components/upload/upload-queue-state'
import { HeatmapCard } from '#/components/questionnaire/overview/heatmap-card'
import {
  defaultScopeFor,
  filterToSearch,
  hasActiveFilter,
  heatmapFilterSearchSchema,
  persistFilter,
  readPersistedFilterSearch,
  searchToFilter,
} from '#/components/questionnaire/overview/heatmap-filter'
import type { HeatmapFilter } from '#/components/questionnaire/overview/heatmap-filter'
import { StatsRow } from '#/components/questionnaire/overview/stats-row'
import { workspaceName } from '#/lib/display-name'

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/workspaces/$workspaceId/',
)({
  validateSearch: heatmapFilterSearchSchema,
  // The URL is the source of truth for the heatmap filter, so browser
  // back/forward restore it for free. When you arrive with no filter in the URL
  // (e.g. via a link that dropped the query), rehydrate the last-applied one
  // from localStorage by redirecting to its URL — keeping a single source of
  // truth and no render-time effect. Self-terminating: after the redirect the
  // URL carries the filter, so the guard below no longer fires.
  beforeLoad: ({ params, search }) => {
    if (!hasActiveFilter(search)) {
      const persisted = readPersistedFilterSearch(params.workspaceId)
      if (hasActiveFilter(persisted)) {
        throw redirect({
          to: '/accounts/$accountId/workspaces/$workspaceId',
          params,
          search: persisted,
          replace: true,
        })
      }
    }
  },
  loader: async ({ context, params }) => {
    const { questionnaire_id: questionnaireId } = context.workspace
    if (questionnaireId) {
      await context.queryClient.ensureQueryData(
        questionnaireDashboardQueryOptions(
          params.accountId,
          params.workspaceId,
          questionnaireId,
        ),
      )
    }
  },
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: WorkspaceOverview,
})

function WorkspaceOverview() {
  const { accountId, workspaceId } = Route.useParams()
  const { workspace } = Route.useRouteContext()
  // The overview's only mutating surface is the document upload pane — gate it
  // on the upload permission (the BFF 403s the upload otherwise).
  const canUpload = useCan('annwn:documents:upload')
  const { t } = useTranslation()
  const uploadDestination = uploadDestinationOf(t, accountId, workspace)

  return (
    <PageContainer width="wide">
      <PageHeader
        title={workspaceName(t, workspace)}
        description={
          workspace.questionnaire_id ? (
            <QuestionnaireLabel
              accountId={accountId}
              workspaceId={workspaceId}
              questionnaireId={workspace.questionnaire_id}
            />
          ) : undefined
        }
      />

      {workspace.questionnaire_id ? (
        <QuestionnaireDashboard
          accountId={accountId}
          workspaceId={workspaceId}
          questionnaireId={workspace.questionnaire_id}
          canUpload={canUpload}
          uploadDestination={uploadDestination}
        />
      ) : canUpload ? (
        <ActionsPane destination={uploadDestination} className="max-w-md" />
      ) : null}
    </PageContainer>
  )
}

function QuestionnaireLabel({
  accountId,
  workspaceId,
  questionnaireId,
}: {
  accountId: string
  workspaceId: string
  questionnaireId: string
}) {
  const { data: questionnaire } = useSuspenseQuery(
    questionnaireDashboardQueryOptions(accountId, workspaceId, questionnaireId),
  )
  return formatCode(questionnaire.code)
}

function QuestionnaireDashboard({
  accountId,
  workspaceId,
  questionnaireId,
  canUpload,
  uploadDestination,
}: {
  accountId: string
  workspaceId: string
  questionnaireId: string
  canUpload: boolean
  uploadDestination: UploadDestination
}) {
  const { data: questionnaire } = useSuspenseQuery(
    questionnaireDashboardQueryOptions(accountId, workspaceId, questionnaireId),
  )
  const { me } = Route.useRouteContext()
  const defaultScope = defaultScopeFor(me)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const filter = searchToFilter(search, defaultScope)

  // Persist for stickiness (rehydrated by beforeLoad when the URL has none) and
  // write to the URL so back/forward restore it. `replace` keeps each tweak
  // from stacking a history entry. Omission in both is relative to the
  // caller's own default, so a Contributor's "All" choice stays explicit and a
  // Reviewer's "Assigned to me" choice stays explicit too.
  const applyFilter = (next: HeatmapFilter) => {
    persistFilter(workspaceId, next, defaultScope)
    void navigate({ search: filterToSearch(next, defaultScope), replace: true })
  }

  const heatmap = (
    <HeatmapCard
      accountId={accountId}
      workspaceId={workspaceId}
      sections={questionnaire.sections ?? []}
      scope={filter.scope}
      status={filter.status}
      onScopeChange={(scope) => applyFilter({ ...filter, scope })}
      onStatusChange={(status) => applyFilter({ ...filter, status })}
    />
  )

  return (
    <>
      <StatsRow questionnaire={questionnaire} />
      {/* Upload pane only for users who can upload; otherwise the heatmap takes
          the full width rather than leaving an empty column. */}
      {canUpload ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">{heatmap}</div>
          <ActionsPane destination={uploadDestination} />
        </div>
      ) : (
        heatmap
      )}
    </>
  )
}
